import {DestroyRef, inject, Injectable, Injector, NgZone} from '@angular/core';
import {NavigationEnd, NavigationStart, Router} from '@angular/router';
import {ASSISTANT_LABEL} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-label';
import {filter, take} from 'rxjs/operators';
import {
    apiErrorEntry,
    AssistantAccess,
    AssistantConsoleError,
    AssistantEnv,
    AssistantFailedRequest,
    AssistantScreen,
    AssistantScreenContext,
    AssistantScreenKind,
    AssistantTrailEntry,
    AssistantTrailType,
    breadcrumbFromUrl,
    capContext,
    clip,
    FormLike,
    formSummary,
    humanize,
    ListViewLike,
    listSummary,
    pathOnly,
    pushTrail,
    singular
} from 'app/blocks/service/assistant-context/assistant-context';
import {
    apiFieldErrors,
    browserInfo,
    clearsFieldErrorsOf,
    consoleErrorEntry,
    consoleText,
    DIAG_MAX,
    DISABLED_ACTIONS_MAX,
    failedRequestEntry,
    firstKeys,
    pushRing,
    recent,
    recordsObservedFailure,
    recordsFailedCall,
    redact,
    redactPath,
    redactTrailEntry,
    REQUIRED_EMPTY_MAX,
    requiredEmptyPaths,
    screenReads,
    SLOW_MAX,
    SLOW_MS,
    sourceFromStack,
    sourceOf,
    tenantDateFormat,
    VISIBLE_ERRORS_MAX,
    watchSignedInUser
} from 'app/blocks/service/assistant-context/assistant-diagnostics';
import {AssistantAttachmentsService} from 'app/blocks/service/assistant-context/assistant-attachments.service';
import {insertAskLinks} from 'app/blocks/service/assistant-context/assistant-ask-targets';
import {isAssistantApiUrl} from 'app/blocks/util/assistant-url';
import {ERROR_DIALOG_CLASS} from 'app/common/dialog/dialog-error';
import {Principal} from 'app/core/auth/principal.service';
// version.json sits outside src (no path alias reaches it), like the toolbar's and Principal's imports
// eslint-disable-next-line no-relative-import-paths/no-relative-import-paths
import versionJson from '../../../../../version.json';

/** Lazy readers a screen hands over; nothing is read until the user sends a message or opens the chat. */
export interface AssistantScreenReaders {
    id?: () => any;
    /** the record's display name (the resource's own discriminator, e.g. order number) */
    label?: () => any;
    list?: () => ListViewLike;
    form?: () => FormLike;
}

interface ScreenSource extends AssistantScreenReaders {
    seq: number;
    kind: AssistantScreenKind;
    entity: string;
}

/**
 * One-line hook for the entity base classes: call from a constructor (injection context). Registers the screen
 * until the component is destroyed; does nothing (never throws) outside an injection context.
 */
export function trackAssistantScreen(kind: AssistantScreenKind, resourceName: string, readers: AssistantScreenReaders): void {
    try {
        inject(AssistantContextService).trackScreen(kind, resourceName, readers, inject(DestroyRef));
    } catch (e) {
        // created outside DI (tests, manual `new`): no context, the screen still works
    }
}

const KIND_PRIORITY: {[k in AssistantScreenKind]: number} = {edit: 3, create: 3, view: 2, list: 1, other: 0};

const CLICKABLE =
    'button, a, [role="button"], [role="menuitem"], [role="tab"], .ant-tabs-tab, .ant-menu-item, .ant-dropdown-menu-item, .mat-mdc-menu-item, th';
const DIALOG_TITLE = '.ant-modal-title, .ant-modal-confirm-title, .mat-mdc-dialog-title, .mat-dialog-title';
const TOAST = '.ngx-toastr, .toast';
const OVERLAY_HOSTS = '.cdk-overlay-container, .overlay-container, #toast-container';
const VISIBLE_ERROR_NODES =
    '.ant-modal-confirm-error .ant-modal-confirm-body, .toast-error, .ant-notification-notice-error, .ant-message-error, .ant-alert-error, ' +
    `.ant-modal.${ERROR_DIALOG_CLASS} di-message-dialog .di-text`;
const DISABLED_ACTIONS =
    'button[disabled], a[disabled], [role="button"][aria-disabled="true"], button[aria-disabled="true"], .mat-mdc-button-disabled, .ant-btn-disabled';
const EMPTY_STATE = '.ant-empty, .ant-table-placeholder, .mat-mdc-no-data-row, .mat-no-data-row, .no-data, .di-no-data';
const FIELD_HOST =
    '.ant-form-item, mat-form-field, .mat-mdc-form-field, di-input, di-select, di-textarea, di-date, di-time, di-checkbox, [diformelement]';
const FIELD_LABEL = 'label, mat-label, .ant-form-item-label, .di-label';

/**
 * Live snapshot of what the user is looking at, for the HaulierMagic Assistant (CU-14zefhy0dgu contract E/F).
 *
 * - The entity base classes (list / view / create-edit) register themselves with one line in their constructor;
 *   the snapshot reads their store slice and form lazily (field NAMES and validation errors only, never values).
 * - Router events give url / title / breadcrumb; a capture-phase document click listener records button and link
 *   labels; overlay watchers record dialog titles and toasts; the HTTP interceptor records failed API calls.
 * - Nothing is stored server-side by the web app. A snapshot is sent only when the user attaches it with the chat's
 *   "Attach current screen context" button (frozen at click time, capped at 4 KB; CU-14zefhy1qnh).
 */
@Injectable({providedIn: 'root'})
export class AssistantContextService {
    private sources: ScreenSource[] = [];
    private seq = 0;
    private trail: AssistantTrailEntry[] = [];
    private seenOverlayNodes = new WeakSet<Element>();
    private watchedHosts = new WeakSet<Element>();
    // ----- attached-context diagnostics (CU-14zefhy1qnh): kept from app start, sent only inside an attached context -----
    private consoleErrors: AssistantConsoleError[] = [];
    private failedRequests: AssistantFailedRequest[] = [];
    private slowRequests: Array<{method: string; path: string; ms: number}> = [];
    /** field errors of the last 400 answer and the page it happened on */
    private lastFieldErrors: {path: string; apiPath: string; errors: Array<{field: string; message: string}>} | null = null;
    private recordingConsole = false;

    constructor(
        private router: Router,
        private zone: NgZone,
        private injector: Injector,
        private attachments: AssistantAttachmentsService
    ) {
        this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
            this.record('nav', pathOnly(e.urlAfterRedirects || e.url));
        });
        // role checks / settings reads belong to the screen being built: start a fresh log when navigation starts
        this.router.events.pipe(filter((e) => e instanceof NavigationStart)).subscribe(() => screenReads.clear());
        if (typeof document !== 'undefined') {
            this.zone.runOutsideAngular(() => {
                document.addEventListener('click', this.onDocumentClick, true);
                this.watchOverlays();
                this.captureConsoleErrors();
                this.observeFailedResources();
            });
        }
        // X2/X3 (CU-14zefhy29r7): another user's sign-in (no logout, expired session, super user switch) starts fresh
        watchSignedInUser(
            () => this.injector.get(Principal, null),
            () => this.reset()
        );
    }

    /**
     * Registers the screen a base-class component shows until `destroyRef` fires (see `trackAssistantScreen`).
     */
    trackScreen(kind: AssistantScreenKind, resourceName: string, readers: AssistantScreenReaders, destroyRef?: DestroyRef): void {
        try {
            const source: ScreenSource = {...readers, kind, entity: this.entityName(resourceName), seq: ++this.seq};
            this.sources.push(source);
            destroyRef?.onDestroy(() => {
                this.sources = this.sources.filter((s) => s !== source);
            });
        } catch (e) {
            // context is best-effort; never break a screen
        }
    }

    /**
     * Records a failed API call (from AssistantContextInterceptor): a trail entry, a `failedRequests` entry and, for a 400
     * with field errors, the errors of this page. 401s and the CoPilot's `chat/events/` polling are skipped; the CoPilot's
     * other failed calls are kept so it can name why its own call failed (CU-14zefhy1r4j), but never as page field errors.
     */
    recordApiError(method: string, url: string, status: number, error: any, durationMs?: number): void {
        try {
            if (!url || status === 401 || !recordsFailedCall(url, status)) {
                return;
            }
            const at = new Date().toISOString();
            // RMA-32: the trail goes to the model like the other diagnostics: redact the server's text (tokens, e-mails)
            this.trail = pushTrail(this.trail, redactTrailEntry(apiErrorEntry(method, url, status, error, at)));
            this.addFailedRequest(failedRequestEntry(method, url, status, error, durationMs ?? null, at));
            const fieldErrors = status === 400 && !isAssistantApiUrl(url) ? apiFieldErrors(error) : [];
            if (fieldErrors.length) {
                this.lastFieldErrors = {path: pathOnly(this.router.url), apiPath: pathOnly(url), errors: fieldErrors};
            }
        } catch (e) {
            // ignore
        }
    }

    /**
     * A page write that succeeded (from AssistantContextInterceptor): the field errors of an earlier 400 on this page are
     * stale then and must not be attached any more (RMA-51). The CoPilot's own calls do not count.
     */
    recordSuccess(method: string, url: string): void {
        try {
            if (this.lastFieldErrors && clearsFieldErrorsOf(this.lastFieldErrors.apiPath, method, url)) {
                this.lastFieldErrors = null;
            }
        } catch (e) {
            // ignore
        }
    }

    /**
     * Forgets everything collected for the signed-in user: trail, console errors, failed and slow calls, field errors,
     * role/setting reads. Called on logout / principal change so the next user never sees the previous user's context (RMA-34).
     */
    reset(): void {
        try {
            this.trail = [];
            this.consoleErrors = [];
            this.failedRequests = [];
            this.slowRequests = [];
            this.lastFieldErrors = null;
            this.seenOverlayNodes = new WeakSet<Element>();
            screenReads.clear();
        } catch (e) {
            // ignore
        }
    }

    /** Records a call that took longer than 5 s (from AssistantContextInterceptor); last 5 kept. */
    recordTiming(method: string, url: string, ms: number): void {
        try {
            if (!url || ms < SLOW_MS || isAssistantApiUrl(url)) {
                return;
            }
            this.slowRequests = pushRing(
                this.slowRequests,
                {method: (method || 'GET').toUpperCase(), path: redactPath(url, 200), ms: Math.round(ms)},
                SLOW_MAX
            );
        } catch (e) {
            // ignore
        }
    }

    /** Adds a trail entry (newest last, last 15 kept). */
    record(t: AssistantTrailType, label: string, detail?: string): void {
        try {
            // RMA-32: click labels, dialog / toast text may hold e-mails or tokens: redact before they are kept
            this.trail = pushTrail(this.trail, redactTrailEntry({t, at: new Date().toISOString(), label, detail}));
        } catch (e) {
            // ignore
        }
    }

    /** What the user is looking at now (≤ 4 KB JSON); attached to a message only on the user's click. */
    snapshot(): AssistantScreenContext {
        // X1 (CU-14zefhy29r7): url, id, record name, title and the breadcrumb built from them are masked like the trail
        const url = redactPath(this.router.url, 2000);
        const screen: AssistantScreen = {kind: 'other'};
        let label = '';
        const src = this.currentSource();
        if (src) {
            screen.kind = src.kind;
            if (src.entity) {
                screen.entity = src.entity;
            }
            const id = this.safe(src.id);
            if (id !== null && id !== undefined && id !== '' && src.kind !== 'create' && src.kind !== 'list') {
                screen.id = redact(id, 40);
            }
            const shown = this.safe(src.label);
            if ((typeof shown === 'string' || typeof shown === 'number') && src.kind !== 'list' && src.kind !== 'create') {
                label = redact(shown, 60);
            }
            const list = src.list ? listSummary(this.safe(src.list)) : undefined;
            if (list) {
                screen.list = list;
            }
            const form = src.form ? formSummary(this.safe(src.form)) : undefined;
            if (form) {
                screen.form = form;
            }
        }
        const ctx: AssistantScreenContext = {url, screen};
        const title = typeof document !== 'undefined' ? redact(document.title, 120) : '';
        if (title) {
            ctx.title = title;
        }
        const crumb = breadcrumbFromUrl(url, label);
        if (crumb.length) {
            ctx.breadcrumb = crumb;
        }
        if (this.trail.length) {
            ctx.trail = this.trail.slice();
        }
        return capContext(ctx);
    }

    /**
     * What the 🧭 button attaches: the screen snapshot plus diagnostics — console errors and failed requests of the last
     * 15 minutes, permissions and disabled actions, fuller validation, errors and empty states on screen, environment and
     * the tenant settings this screen read. Redacted; the caller caps the item (6 KB). `errorText` (from "Ask CoPilot")
     * is added to the errors on screen when the page no longer shows it. Never throws: a failing reader is left out.
     */
    attachSnapshot(errorText?: string): AssistantScreenContext {
        const ctx = this.snapshot();
        const now = Date.now();
        const part = <T>(read: () => T): T => {
            try {
                return read();
            } catch (e) {
                return undefined;
            }
        };
        const consoleErrors = recent(this.consoleErrors, now);
        if (consoleErrors.length) {
            ctx.consoleErrors = consoleErrors;
        }
        const failedRequests = recent(this.failedRequests, now);
        if (failedRequests.length) {
            ctx.failedRequests = failedRequests;
        }
        const access = part(() => this.readAccess());
        if (access) {
            ctx.access = access;
        }
        part(() => this.addFormDetails(ctx));
        const visibleErrors = part(() => this.readVisibleErrors(errorText)) || [];
        if (visibleErrors.length) {
            ctx.visibleErrors = visibleErrors;
        }
        const empty = part(() => this.readEmptyState(ctx));
        if (empty) {
            ctx.emptyState = empty;
        }
        const env = part(() => this.readEnv());
        if (env) {
            ctx.env = env;
        }
        if (screenReads.settings.size) {
            ctx.settings = firstKeys(screenReads.settings, 30);
        }
        return ctx;
    }

    // ----- attached-context collectors ----------------------------------------------------------------

    private addFailedRequest(entry: AssistantFailedRequest): void {
        const t = Date.parse(entry.at);
        // the same failure seen by both the interceptor and the resource observer is kept once
        const seen = this.failedRequests.some((e) => e.path === entry.path && e.status === entry.status && Math.abs(Date.parse(e.at) - t) < 10000);
        if (!seen) {
            this.failedRequests = pushRing(this.failedRequests, entry, DIAG_MAX);
        }
    }

    private recordConsoleError(message: string, source: string): void {
        if (this.recordingConsole) {
            return;
        }
        this.recordingConsole = true;
        try {
            const entry = consoleErrorEntry(message, source, new Date().toISOString());
            if (entry) {
                this.consoleErrors = pushRing(this.consoleErrors, entry, DIAG_MAX);
            }
        } catch (e) {
            // ignore
        } finally {
            this.recordingConsole = false;
        }
    }

    /** window errors, unhandled rejections and console.error calls (the original console.error always runs). */
    private captureConsoleErrors(): void {
        if (typeof window === 'undefined' || typeof console === 'undefined') {
            return;
        }
        window.addEventListener('error', (e: ErrorEvent) => {
            // resource load errors (img/script) come without a message: the resource observer covers those
            if (e && e.message) {
                this.recordConsoleError(e.message, sourceOf(e.filename, e.lineno, e.colno) || sourceFromStack(e.error && e.error.stack));
            }
        });
        window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
            const reason = e && e.reason;
            this.recordConsoleError('Unhandled promise rejection: ' + consoleText([reason]), sourceFromStack(reason && reason.stack));
        });
        const original = console.error;
        if ((original as any).__hmAssistantWrapped) {
            return;
        }
        const wrapped = (...args: any[]) => {
            try {
                const error = args.find((a) => a && typeof a.stack === 'string');
                this.recordConsoleError(consoleText(args), sourceFromStack(error && error.stack));
            } catch (e) {
                // never get in the way of logging
            }
            return original.apply(console, args);
        };
        (wrapped as any).__hmAssistantWrapped = true;
        console.error = wrapped;
    }

    /**
     * Failed loads that do not go through HttpClient (scripts, iframes, sockets…) where the browser reports the status.
     * Images, fonts and map tiles count only from 500 up.
     */
    private observeFailedResources(): void {
        if (typeof PerformanceObserver === 'undefined') {
            return;
        }
        try {
            new PerformanceObserver((list) => {
                list.getEntries().forEach((entry: any) => {
                    const status = entry && entry.responseStatus;
                    // X3 (CU-14zefhy29r7): 401s are skipped here too, like recordApiError does
                    if (!recordsObservedFailure(entry && entry.name, status, entry && entry.initiatorType)) {
                        return;
                    }
                    this.addFailedRequest(
                        failedRequestEntry(
                            'GET',
                            entry.name,
                            status,
                            null,
                            entry.duration,
                            new Date(performance.timeOrigin + entry.startTime).toISOString()
                        )
                    );
                });
            }).observe({type: 'resource', buffered: true} as any);
        } catch (e) {
            // not supported: HttpClient failures are still recorded by the interceptor
        }
    }

    private isVisible(el: Element): boolean {
        if (!el || el.closest('[data-ai-ignore]')) {
            return false;
        }
        const rects = (el as HTMLElement).getClientRects ? (el as HTMLElement).getClientRects() : null;
        return !!rects && rects.length > 0;
    }

    private readAccess(): AssistantAccess | undefined {
        const access: AssistantAccess = {};
        const role = this.roleName();
        if (role) {
            access.role = role;
        }
        if (screenReads.checks.size) {
            access.checks = firstKeys(screenReads.checks, 30);
            const hidden: string[] = [];
            screenReads.checks.forEach((v, k) => !v && /-show$/.test(k) && hidden.length < DISABLED_ACTIONS_MAX && hidden.push(k));
            if (hidden.length) {
                access.hiddenActions = hidden;
            }
        }
        const disabled: Array<{label: string; reason?: string}> = [];
        if (typeof document !== 'undefined') {
            document.querySelectorAll(DISABLED_ACTIONS).forEach((el) => {
                if (disabled.length >= DISABLED_ACTIONS_MAX || !this.isVisible(el)) {
                    return;
                }
                const h = el as HTMLElement;
                const label = clip(h.getAttribute('aria-label') || h.innerText || h.textContent || '', 60);
                if (!label || disabled.some((d) => d.label === label)) {
                    return;
                }
                const tipHost = h.closest('[nztooltiptitle], [mattooltip], [title]') as HTMLElement;
                const reasonText =
                    h.getAttribute('title') ||
                    h.getAttribute('nztooltiptitle') ||
                    h.getAttribute('mattooltip') ||
                    (tipHost && (tipHost.getAttribute('nztooltiptitle') || tipHost.getAttribute('mattooltip') || tipHost.getAttribute('title'))) ||
                    '';
                const item: {label: string; reason?: string} = {label};
                const reason = redact(reasonText, 120);
                if (reason && reason !== label) {
                    item.reason = reason;
                }
                disabled.push(item);
            });
        }
        if (disabled.length) {
            access.disabledActions = disabled;
        }
        return Object.keys(access).length ? access : undefined;
    }

    private roleName(): string {
        try {
            const principal = this.injector.get(Principal, null);
            let identity: any = null;
            principal
                ?.getAuthenticationState()
                .pipe(take(1))
                .subscribe((id) => (identity = id));
            const roles = identity && (identity.roles || identity.authorities);
            return clip(Array.isArray(roles) ? roles.join(', ') : roles || '', 120);
        } catch (e) {
            return '';
        }
    }

    /** Required fields still empty (by LABEL), the last 400's field errors on this page and the stepper position. */
    private addFormDetails(ctx: AssistantScreenContext): void {
        const src = this.currentSource();
        const form = src && src.form ? this.safe(src.form) : null;
        const screen = ctx.screen;
        const details: {requiredEmpty?: string[]; apiFieldErrors?: Array<{field: string; message: string}>; step?: any} = {};
        if (form) {
            const labels = requiredEmptyPaths(form)
                .map((path) => this.fieldLabel(path))
                .filter((l, i, all) => l && all.indexOf(l) === i)
                .slice(0, REQUIRED_EMPTY_MAX);
            if (labels.length) {
                details.requiredEmpty = labels;
            }
        }
        if (this.lastFieldErrors && this.lastFieldErrors.path === pathOnly(this.router.url)) {
            details.apiFieldErrors = this.lastFieldErrors.errors;
        }
        const step = this.readStep();
        if (step) {
            details.step = step;
        }
        if (Object.keys(details).length) {
            screen.form = {...(screen.form || {}), ...details};
        }
    }

    /** "customer.name" → the label shown next to that field, else "Name". */
    private fieldLabel(path: string): string {
        const name = String(path).split('.').pop();
        if (typeof document !== 'undefined' && name && !/^\d+$/.test(name)) {
            const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(name) : name.replace(/"/g, '');
            const field = document.querySelector(`[formcontrolname="${esc}"], [field="${esc}"]`);
            const host = field && field.closest(FIELD_HOST);
            const label = host && host.querySelector(FIELD_LABEL);
            const text = label
                ? clip((label as HTMLElement).innerText || label.textContent || '', 60)
                      .replace(/[*:]+$/, '')
                      .trim()
                : '';
            if (text) {
                return text;
            }
        }
        return clip(humanize(name || path), 60);
    }

    private readStep(): {index: number; count: number; title?: string} | undefined {
        if (typeof document === 'undefined') {
            return undefined;
        }
        const steps = Array.from(document.querySelectorAll('.ant-steps')).find((s) => this.isVisible(s));
        if (steps) {
            const items = Array.from(steps.querySelectorAll('.ant-steps-item'));
            const at = items.findIndex((i) => i.classList.contains('ant-steps-item-process') || i.classList.contains('ant-steps-item-active'));
            if (items.length && at >= 0) {
                const title = items[at].querySelector('.ant-steps-item-title') as HTMLElement;
                return {index: at + 1, count: items.length, ...(title ? {title: clip(title.innerText || title.textContent, 60)} : {})};
            }
        }
        const headers = Array.from(document.querySelectorAll('.mat-step-header')).filter((h) => this.isVisible(h));
        const current = headers.findIndex((h) => h.getAttribute('aria-selected') === 'true');
        if (headers.length && current >= 0) {
            const label = headers[current].querySelector('.mat-step-label') as HTMLElement;
            return {index: current + 1, count: headers.length, ...(label ? {title: clip(label.innerText || label.textContent, 60)} : {})};
        }
        return undefined;
    }

    private readVisibleErrors(extra?: string): string[] {
        const out: string[] = [];
        if (typeof document !== 'undefined') {
            document.querySelectorAll(VISIBLE_ERROR_NODES).forEach((el) => {
                if (out.length >= VISIBLE_ERRORS_MAX || !el.isConnected) {
                    return;
                }
                const text = redact(((el as HTMLElement).innerText || el.textContent || '').split(ASSISTANT_LABEL).join(''), 300);
                if (text && out.indexOf(text) < 0) {
                    out.push(text);
                }
            });
        }
        const asked = extra ? redact(extra, 300) : '';
        if (asked && !out.some((t) => t.indexOf(asked) >= 0 || asked.indexOf(t) >= 0)) {
            out.unshift(asked);
        }
        return out.slice(0, VISIBLE_ERRORS_MAX);
    }

    private readEmptyState(ctx: AssistantScreenContext): AssistantScreenContext['emptyState'] {
        if (typeof document === 'undefined') {
            return undefined;
        }
        const node = Array.from(document.querySelectorAll(EMPTY_STATE)).find((el) => this.isVisible(el));
        if (!node) {
            return undefined;
        }
        const empty: AssistantScreenContext['emptyState'] = {};
        const text = clip((node as HTMLElement).innerText || node.textContent || '', 120);
        if (text) {
            empty.text = text;
        }
        const list = ctx.screen && ctx.screen.list;
        if (list && list.filters) {
            empty.filters = list.filters;
        }
        if (list && list.search) {
            empty.search = list.search;
        }
        return empty;
    }

    private readEnv(): AssistantEnv {
        const env: AssistantEnv = {};
        if (versionJson && versionJson.appVersion) {
            env.appVersion = clip(versionJson.appVersion, 40);
        }
        if (typeof navigator !== 'undefined') {
            Object.assign(env, browserInfo(navigator.userAgent));
            if (navigator.language) {
                env.locale = clip(navigator.language, 20);
            }
            env.online = navigator.onLine !== false;
        }
        if (typeof window !== 'undefined') {
            if (window.screen) {
                env.screen = `${window.screen.width}x${window.screen.height}`;
            }
            env.viewport = `${window.innerWidth}x${window.innerHeight}`;
        }
        try {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (tz) {
                env.timeZone = clip(tz, 60);
            }
        } catch (e) {
            // ignore
        }
        // the tenant's configured date format (from the chat state), so the CoPilot reads and writes dates the tenant's way
        const dateFormat = tenantDateFormat(this.attachments.dateFormat);
        if (dateFormat) {
            env.dateFormat = dateFormat;
        }
        if (this.slowRequests.length) {
            env.slowRequests = this.slowRequests.slice();
        }
        return env;
    }

    // ----- internals ----------------------------------------------------------------------------------

    /** The most specific live screen: edit/create beat view beat list (a list embedded in an edit page); newest wins ties. */
    private currentSource(): ScreenSource | undefined {
        let best: ScreenSource;
        for (const s of this.sources) {
            if (
                !best ||
                KIND_PRIORITY[s.kind] > KIND_PRIORITY[best.kind] ||
                (KIND_PRIORITY[s.kind] === KIND_PRIORITY[best.kind] && s.seq > best.seq)
            ) {
                best = s;
            }
        }
        return best;
    }

    private entityName(resourceName: string): string {
        return singular(humanize(resourceName || '')).replace(/\s+/g, '');
    }

    private safe(reader: () => any): any {
        try {
            return reader ? reader() : undefined;
        } catch (e) {
            return undefined;
        }
    }

    private onDocumentClick = (event: MouseEvent): void => {
        try {
            const el = event.target as Element;
            if (!el || typeof el.closest !== 'function') {
                return;
            }
            // the chat itself, fields marked private and form inputs are never recorded
            if (el.closest('[data-ai-ignore], [data-ai-private], input, textarea, select, [contenteditable="true"]')) {
                return;
            }
            const target = el.closest(CLICKABLE) as HTMLElement;
            if (!target) {
                return;
            }
            const label =
                target.getAttribute('aria-label') ||
                target.getAttribute('title') ||
                target.getAttribute('nztooltiptitle') ||
                target.getAttribute('mattooltip') ||
                target.innerText ||
                target.textContent ||
                '';
            const text = clip(label, 80);
            if (text) {
                this.record('click', text);
            }
        } catch (e) {
            // ignore
        }
    };

    /** Watches the overlay containers (nz-modal, toastr) for dialog titles and toast messages. */
    private watchOverlays(): void {
        if (typeof MutationObserver === 'undefined' || !document.body) {
            return;
        }
        const scan = () => document.querySelectorAll(OVERLAY_HOSTS).forEach((host) => this.watchHost(host));
        scan();
        new MutationObserver(scan).observe(document.body, {childList: true});
    }

    private watchHost(host: Element): void {
        if (this.watchedHosts.has(host)) {
            return;
        }
        this.watchedHosts.add(host);
        new MutationObserver((records) => {
            let added = false;
            // our own "Ask CoPilot" link is not new content
            records.forEach((r) => (added = added || Array.from(r.addedNodes).some((n) => !(n as Element).classList?.contains('hm-ask-copilot'))));
            if (added) {
                // content renders a tick after the node is attached
                setTimeout(() => this.readOverlays(host), 80);
            }
        }).observe(host, {childList: true, subtree: true});
    }

    private readOverlays(host: Element): void {
        try {
            host.querySelectorAll(DIALOG_TITLE).forEach((titleEl) => {
                if (this.seenOverlayNodes.has(titleEl) || titleEl.closest('[data-ai-ignore]')) {
                    return;
                }
                this.seenOverlayNodes.add(titleEl);
                const content = titleEl.closest('.ant-modal-confirm-body')?.querySelector('.ant-modal-confirm-content');
                this.record(
                    'dialog',
                    (titleEl as HTMLElement).innerText || titleEl.textContent,
                    content ? (content as HTMLElement).innerText : undefined
                );
            });
            host.querySelectorAll(TOAST).forEach((toast) => {
                if (this.seenOverlayNodes.has(toast)) {
                    return;
                }
                const title = toast.querySelector('.toast-title') as HTMLElement;
                const message = toast.querySelector('.toast-message') as HTMLElement;
                const titleText = title ? title.innerText || title.textContent : '';
                const messageText = message ? message.innerText || message.textContent : '';
                if (!titleText && !messageText) {
                    return; // not rendered yet; read on the next mutation
                }
                this.seenOverlayNodes.add(toast);
                this.record('toast', titleText || messageText, titleText ? messageText : undefined);
            });
            this.addAskLinks(host);
        } catch (e) {
            // ignore
        }
    }

    /**
     * "Ask CoPilot" on error modals (`NzModalService.error`), confirm modals styled as errors, the app's own message
     * dialog when it shows an error, and error notifications / messages (toastr error toasts have it in their own
     * template). One global hook, no per-screen code; see `assistant-ask-targets`. Only while the CoPilot is enabled for
     * the user. Click → the chat opens with this screen attached (the modal text included) and the question prefilled;
     * a dialog is then closed with its own button (a confirm is cancelled, never confirmed).
     */
    private addAskLinks(host: Element): void {
        if (!this.attachments.copilotEnabled) {
            return;
        }
        insertAskLinks(host, document, (text) => this.zone.run(() => this.attachments.askAboutError(clip(text, 300))));
    }
}
